import { useLiveQuery } from "@tanstack/react-db";
import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";

import { createWorkspaceTemplate, deleteWorkspaceTemplate, updateWorkspaceTemplate } from "@storyboard/web-client";
import { allTemplatesSplit, UseTemplates, WsTemplateExtended } from "@/components/Templates/templates";
import { ampli } from "@/config";
import { transport } from "@/config.ts";
import { bulkUpsertTemplate, cachedTemplatesQuery, deleteTemplateById } from "@/models/repositories";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider.tsx";
import { UserContext } from "@/models/StateProviders/userProvider.tsx";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider.tsx";

export default function useTemplates(): UseTemplates {
  const { user, workspaceMembership } = useContext(UserContext);
  const { myAccountId } = useContext(MyAccountContext);
  const { currentWorkspaceId } = useContext(WorkspaceContext);
  const { data: templateRows } = useLiveQuery((q) =>
    currentWorkspaceId ? cachedTemplatesQuery(currentWorkspaceId)(q) : null,
  );
  const templates: WsTemplateExtended[] = templateRows ?? [];
  const canEditOthers = workspaceMembership.role === "admin";

  const createMutation = useMutation({
    mutationFn: ({ name, template }: { name: string; template: string }) =>
      createWorkspaceTemplate(transport, { workspaceId: currentWorkspaceId, name, template }),
    onSuccess: async (response) => {
      await bulkUpsertTemplate([response?.template]);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, name, template }: { id: string; name: string; template: string }) =>
      updateWorkspaceTemplate(transport, { workspaceId: currentWorkspaceId, templateId: id, name, template }),
    onSuccess: async (response) => {
      await bulkUpsertTemplate([response?.template]);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteWorkspaceTemplate(transport, { workspaceId: currentWorkspaceId, templateId: id }),
    onSuccess: async (_response, id) => {
      await deleteTemplateById(id);
    },
  });

  const extendTemplate = <T extends object | undefined>(template: T) => ({
    ...template,
    canEdit: true,
    authorName: user.name,
    role: workspaceMembership.role,
  });

  const saveTemplate = async ({ name, template }: { name: string; template: string }) => {
    try {
      const response = await createMutation.mutateAsync({ name, template });
      ampli.createTemplate();
      return extendTemplate(response?.template);
    } catch {
      return Promise.reject({ message: "Could not save template. Please try again." });
    }
  };

  const deleteTemplate = async (id: string): Promise<null> => {
    try {
      await deleteMutation.mutateAsync(id);
      ampli.deleteTemplate();
      return null;
    } catch {
      return Promise.reject({ message: "Could not delete template. Please try again." });
    }
  };

  const updateTemplate = async ({ id, name, template }: { id: string; name: string; template: string }) => {
    try {
      const response = await updateMutation.mutateAsync({ id, name, template });
      ampli.saveTemplate();
      return extendTemplate(response?.template);
    } catch {
      return Promise.reject({ message: "Could not update template. Please try again." });
    }
  };

  const cloneTemplate = async (id: string) => {
    const foundTemplate = templates?.find((t) => t.id === id);
    if (!foundTemplate) {
      return Promise.reject({ message: "Template not found." });
    }
    try {
      const response = await createMutation.mutateAsync({
        name: `(CLONED) ${foundTemplate.name}`,
        template: foundTemplate.template,
      });
      ampli.cloneTemplate();
      return { ...extendTemplate(response?.template), cloned: true };
    } catch {
      return Promise.reject({ message: "Could not clone template. Please try again." });
    }
  };

  return {
    count: templates?.length || 0,
    templates: allTemplatesSplit({ templates, myAccountId, canEditOthers }),
    cloneTemplate,
    saveTemplate,
    deleteTemplate,
    updateTemplate,
  };
}
